import { EMAIL_RE } from '../utils/valueFormats';
import React, { useEffect } from 'react';
import { redirect, Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useState } from 'react';
import {
    Container, Typography, FormControl, FormGroup, FormControlLabel, Tooltip, Table, TableBody, TableCell, Link,
    Checkbox, InputLabel, Select, MenuItem, RadioGroup, Radio, Button, Fade, TableContainer, TableHead, Snackbar,
    useMediaQuery, Grid, TextField, Autocomplete, Chip, CircularProgress, DialogContentText, TableRow,
    Backdrop, FormHelperText, Paper, Divider, List, ListItem, ListItemText, LinearProgress, Alert, InputAdornment,
    ListItemButton, ListItemIcon, Box, Dialog, DialogTitle, DialogContent, DialogActions, GlobalStyles
} from '@mui/material';
import fetchData from '../utils/fetchData';
import { getAuth, reload, sendPasswordResetEmail, signInWithEmailAndPassword, createUserWithEmailAndPassword, sendEmailVerification } from "firebase/auth";
import TurnStile from '../components/TurnStile';
import { auth, db } from '../config/firebase';
import { collection, getDocs, query, where, doc, setDoc, getDoc } from 'firebase/firestore';
import logo_no_tagline from "../assets/logo_no_tagline.jpg";

const ForgetPassword = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState();
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [emailFocused, setEmailFocused] = useState(false);
    const [sending, setSending] = useState(false);

    /*
     * The app's rule, not a looser local one. This was `/^\S+@\S+\.\S+$/`, and
     * `\S` matches an @ — so "a@b@c.d" was accepted at sign-up and refused by
     * every screen afterwards.
     */
    const validateEmail = (email) => EMAIL_RE.test(String(email ?? '').trim());

    const sendCustomResetEmail = async (email) => {
        const apiUrl = `${process.env.REACT_APP_API_BASE_URL}xeo/sendResetEmail`;

        try {
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email }),
            });

            // Parse JSON body if your API returns JSON
            const data = await response.json();

            // Return a standardized object
            return {
                ok: response.ok,       // HTTP status success
                message: data.message || (response.ok ? "Success" : "Failed"),
            };
        } catch (error) {
            // Network or other errors
            return {
                ok: false,
                message: error.message || "Something went wrong",
            };
        }
    }

    const resetPassword = async (event) => {
        event.preventDefault();

        if (!email) {
            setEmailError('Email is required');
            return;
        } else if (!validateEmail(email)) {
            setEmailError('Invalid email format');
            return;
        }
        //send reset email
        // const auth = getAuth();
        try {
            setSending(true);
            const response = await sendCustomResetEmail(email);

            if (!response.ok) {
                setSnackbarMessage(response.message || "Failed to send password reset email.");
                setSnackbarOpen(true);
            } else {
                setSnackbarMessage("If an account with this email exists, you’ll receive a password reset link.");
                setSnackbarOpen(true);
            }
        } catch (error) {
            console.error("Error sending password reset email:", error);

            // Handle specific Firebase errors
            let message = "";
            switch (error.code) {
                case "auth/missing-email":
                    message = "Please enter your email address.";
                    break;
                case "auth/invalid-email":
                    message = "Please enter a valid email address.";
                    break;
                case "auth/user-not-found":
                    message = "No user found with this email.";
                    break;
                default:
                    message = "Something went wrong. Please try again.";
            }
            setSnackbarMessage(message);
            setSnackbarOpen(true);
        } finally {
            setSending(false);
        }
    };

    return (
        <Box sx={{
            // Fill the viewport exactly with position:fixed + inset:0 so no
            // scrollbar can ever appear. Content sits slightly above center.
            position: 'fixed',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            // Scrolls INSIDE the frame. It was overflow:hidden, so whatever didn't
            // fit — a phone in landscape, or the form with the keyboard up — was
            // cut off with no way to reach it. Scrollbars are hidden app-wide, so
            // on a screen where it fits nothing changes.
            overflowX: 'hidden',
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            backgroundColor: 'var(--surface)',
            backgroundImage:
                'radial-gradient(ellipse 55% 38% at 50% 60%, rgba(25,118,210,0.12), transparent 70%),' +
                'radial-gradient(circle at 100% 0%, rgba(25,118,210,0.06), transparent 35%),' +
                'radial-gradient(circle at 0% 100%, rgba(13,71,161,0.05), transparent 40%)',
            px: { xs: 2.5, sm: 3 },
            pt: { xs: '5vh', sm: '8vh' },
            pb: 2,
        }}>
            <Fade in timeout={500}>
            <Box sx={{ width: '100%', maxWidth: 440, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, position: 'relative', zIndex: 1 }}>
                <Box component="img" src={logo_no_tagline} alt="XEO"
                    sx={{
                        width: { xs: 110, sm: 130 },
                        height: 'auto',
                        mixBlendMode: 'multiply',
                        WebkitMaskImage: 'radial-gradient(ellipse 70% 70% at 50% 50%, #000 55%, transparent 100%)',
                        maskImage: 'radial-gradient(ellipse 70% 70% at 50% 50%, #000 55%, transparent 100%)',
                    }} />

                <Box
                    component="form"
                    noValidate
                    onSubmit={resetPassword}
                    sx={{
                        width: '100%',
                        backgroundColor: 'var(--surface)',
                        px: { xs: 3, sm: 4.5 },
                        py: { xs: 3, sm: 3.5 },
                        borderRadius: 3,
                        border: '1px solid var(--rule)',
                        boxShadow: '0 20px 60px rgba(15,31,61,0.10), 0 4px 16px rgba(25,118,210,0.06)',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0, left: 0, right: 0,
                            height: '4px',
                            background: 'linear-gradient(90deg, #1976d2 0%, #1565c0 50%, #0d47a1 100%)',
                        },
                    }}
                >
                    <Typography sx={{ fontSize: { xs: '1.25rem', sm: '1.6rem' }, fontWeight: 800, color: 'var(--ink)', textAlign: 'center', mb: 0.75, letterSpacing: '-0.015em' }}>
                        Reset password
                    </Typography>
                    <Typography sx={{ fontSize: '0.88rem', color: 'var(--ink-3)', textAlign: 'center', mb: 3.5 }}>
                        Enter your email and we'll send you a reset link
                    </Typography>
                    {/* Email Field. autoComplete="username" lets a phone's password
                        manager offer the email of the saved login. */}
                    <TextField
                        label="Email address"
                        name="email"
                        type="email"
                        autoComplete="username"
                        value={email}
                        onChange={(e) => {
                            setEmail(e.target.value);
                            setEmailError("");
                        }}
                        onFocus={() => setEmailFocused(true)}
                        onBlur={() => setEmailFocused(false)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        error={!!emailError}
                        helperText={emailError || "Enter the email associated with your account"}
                        sx={{ mb: 2 }}
                    />

                    {/* Send Reset Button */}
                    <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        fullWidth
                        disabled={sending}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '0.95rem',
                            borderRadius: 2,
                            py: 1.35,
                            mt: 0.5,
                            boxShadow: '0 4px 14px rgba(25,118,210,0.22)',
                            '&:hover': { boxShadow: '0 6px 20px rgba(25,118,210,0.32)' },
                        }}
                    >
                        {sending ? (
                            <CircularProgress size={20} sx={{ color: 'white' }} />
                        ) : (
                            'Send Reset Email'
                        )}
                    </Button>

                    {/* Divider with text */}
                    <Divider sx={{ my: 4 }} />

                    {/* Login Section */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            Already reset your password?
                        </Typography>
                        <Button
                            component={RouterLink}
                            to="/"
                            variant="outlined"
                            color="primary"
                            size="medium"
                            sx={{
                                textTransform: "none",
                                borderRadius: 2,
                                ml: 2,
                                px: 3,
                            }}
                        >
                            Login
                        </Button>
                    </Box>
                </Box>
            </Box>
            </Fade>
            <Snackbar
                open={snackbarOpen}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                sx={{ mt: 5, mr: 1 }}
                onClose={() => setSnackbarOpen(false)}
            // message={snackbarMessage}
            >
                <Alert
                    onClose={() => setSnackbarOpen(false)}
                    severity={snackbarMessage.includes("If an account") ? "info" : "error"}
                    sx={{ width: '100%', fontSize: '1.0rem' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    )
}

export default ForgetPassword;